<template>
  <!-- Top of the contract page, same layout as the system and third-party heroes (US 9371, US 9370) -->
  <header class="sx-hero" qid="contract-hero">
    <component :is="vendor ? 'router-link' : 'span'"
               :to="vendor ? vendorRoute(vendor) : null"
               class="sx-hero__logo"
               :class="{ 'has-image': vendor && vendor.logo }"
               :title="vendor ? vendor.name : ''"
               qid="contract-hero-logo">
      <img v-if="vendor && vendor.logo" :src="vendor.logo" alt="">
      <font-awesome-icon v-else icon="file-alt" class="sx-hero__file"/>
    </component>

    <div class="sx-hero__main">
      <router-link :to="{ name: 'contracts' }" class="sx-hero__back" qid="contract-back">
        <font-awesome-icon icon="angle-left"/>{{ $t('maintenance.third_parties.title') }}
      </router-link>
      <h1 class="sx-hero__name" qid="tp-view-page-title">{{ contract.name }}</h1>
      <p v-if="contract.description" class="sx-hero__text">{{ contract.description | truncate(220) }}</p>
      <div class="sx-hero__tags">
        <span v-if="contract.ended_at" class="sx-hero__tag is-retired" qid="contract-hero-ended"><font-awesome-icon icon="archive"/>{{ $t('contract_end.ended_on', { date: endedOn(contract.ended_at) }) }}</span>
        <router-link v-for="item in vendors"
                     :key="item.id"
                     :to="vendorRoute(item)"
                     class="sx-hero__tag is-mint"
                     qid="contract-hero-vendor">
          <font-awesome-icon icon="building"/>{{ item.name | truncate(40) }}
        </router-link>
        <span v-if="rolesLabel" class="sx-hero__tag"><font-awesome-icon icon="user-shield"/>{{ rolesLabel }}</span>
        <span v-if="getClient.gdprModule && contract.data_processor_agreement === 'Yes'" class="sx-hero__tag"><font-awesome-icon icon="check"/>{{ $t('contract_hero.dpa') }}</span>
        <span v-if="contract.reference" class="sx-hero__tag"><font-awesome-icon icon="id-card"/>{{ contract.reference | truncate(30) }}</span>
        <span v-if="contract.type" class="sx-hero__tag"><font-awesome-icon icon="shapes"/>{{ contract.type | truncate(30) }}</span>
        <span v-if="!contract.partition && getClient.parent" class="sx-hero__tag"><font-awesome-icon icon="lock"/>{{ $t('systems_overview.from_workspace', { name: getClient.parent.name }) }}</span>
        <span v-for="tag in shownTags" :key="tag.id || tag.name_translated" class="sx-hero__tag" :title="tag.name_translated"><font-awesome-icon icon="tag"/>{{ tag.name_translated | truncate(30) }}</span>
        <span v-if="hiddenTags" class="sx-hero__tag">+{{ hiddenTags }}</span>
        <slot name="task"></slot>
      </div>
    </div>

    <div class="sx-hero__people" qid="contract-owners-list">
      <small>{{ $t('system_hero.people') }}</small>
      <template v-if="people.length">
        <div class="sx-hero__avatars">
          <Avatar v-for="user in people.slice(0, 4)" :key="user.id" :object="user" size="sm" :noOwner="true"></Avatar>
        </div>
        <span class="sx-hero__who">
          {{ owner ? owner.name : $t('system_hero.no_owner') }}
          <span v-if="people.length > 1" class="sx-hero__faint">{{ $t('system_hero.more_people', { count: people.length - 1 }) }}</span>
        </span>
      </template>
      <span v-else class="sx-hero__who is-warn"><font-awesome-icon icon="exclamation-triangle"/>{{ $t('system_hero.nobody') }}</span>
    </div>

    <div class="sx-hero__done" qid="contract-completeness">
      <svg class="sx-hero__ring" viewBox="0 0 76 76" aria-hidden="true">
        <circle cx="38" cy="38" r="32" fill="none" stroke="rgba(255,255,255,.18)" stroke-width="7"/>
        <circle v-if="done"
                cx="38" cy="38" r="32" fill="none" stroke="#7FE0C4" stroke-width="7" stroke-linecap="round"
                transform="rotate(-90 38 38)"
                :stroke-dasharray="ringDash"/>
      </svg>
      <div class="sx-hero__done-text is-clamped" :title="missingText">
        <b>{{ $t('system_hero.filled', { done: done, total: checks.length }) }}</b>
        <span>{{ missingText }}</span>
      </div>
    </div>

    <div class="sx-hero__act">
      <div class="sx-hero__row">
        <button v-if="canEdit"
                type="button"
                class="sx-hero__btn sx-hero__grow"
                qid="edit-tp-button"
                @click="$emit('edit')">
          <font-awesome-icon icon="pen"/>{{ $t('system_hero.edit') }}
        </button>
        <b-dropdown right
                    no-caret
                    variant="link"
                    toggle-class="sx-hero__btn"
                    menu-class="sx-menu"
                    qid="contract-more">
          <template #button-content>
            <font-awesome-icon icon="ellipsis-h"/><span class="sr-only">{{ $t('system_hero.more') }}</span>
          </template>
          <b-dropdown-item v-if="canEdit" @click="$emit('edit')"><font-awesome-icon icon="pen"/>{{ $t('system_hero.menu.edit') }}</b-dropdown-item>
          <b-dropdown-item v-for="item in vendors" :key="'open-' + item.id" :to="vendorRoute(item)"><font-awesome-icon icon="building"/>{{ $t('contract_hero.menu.open_vendor', { name: item.name }) }}</b-dropdown-item>
          <b-dropdown-item v-if="canEdit" qid="contract-move" @click="$emit('move')"><font-awesome-icon icon="exchange-alt"/>{{ $t('contract_hero.menu.move') }}</b-dropdown-item>
          <b-dropdown-item qid="contract-print" @click="print"><font-awesome-icon icon="file-alt"/>{{ $t('system_hero.menu.export') }}</b-dropdown-item>
          <b-dropdown-item v-if="canEdit && !contract.ended_at" qid="contract-end" @click="$emit('end')"><font-awesome-icon icon="archive"/>{{ $t('contract_hero.menu.end') }}</b-dropdown-item>
          <b-dropdown-item v-if="canEdit && contract.ended_at" qid="contract-reopen" @click="$emit('reopen')"><font-awesome-icon icon="undo"/>{{ $t('contract_hero.menu.reopen') }}</b-dropdown-item>
          <template v-if="canDelete">
            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item link-class="is-danger" qid="delete-tp-button" @click="$emit('delete')"><font-awesome-icon icon="trash-alt"/>{{ $t('system_hero.menu.delete') }}</b-dropdown-item>
          </template>
        </b-dropdown>
      </div>
    </div>
  </header>
</template>

<script>
  import Avatar from '@/components/Avatar.vue';
  import {endedOn} from '@/util/contractEnd.js';

  export default {
    name: 'ContractHero',
    components: {
      Avatar
    },
    props: {
      contract: { type: Object, required: true }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      editable() {
        return !this.getClient.parent || this.contract.partition;
      },
      canEdit() {
        return this.isGranted([this.permissions.THIRD_PARTY_EDIT, this.permissions.THIRD_PARTY_EDIT_OWN], this.contract) && this.editable;
      },
      canDelete() {
        return this.isGranted([this.permissions.THIRD_PARTY_DELETE, this.permissions.THIRD_PARTY_DELETE_OWN], this.contract) && this.editable;
      },
      vendors() {
        return this.contract.vendors || [];
      },
      // The company the contract is with; its logo is the contract's tile
      vendor() {
        return this.vendors[0] || null;
      },
      // Processor, controller…: from the links to systems and activities
      rolesLabel() {
        let roles = this.contract.processing_roles || [];
        return roles.map(role => this.$t('maintenance.third_parties.processing_role.' + this.$options.filters.slugify(role))).join(', ');
      },
      tags() {
        return this.contract.tags_translated || [];
      },
      shownTags() {
        return this.tags.slice(0, 3);
      },
      hiddenTags() {
        return Math.max(this.tags.length - 3, 0);
      },
      owner() {
        return (this.contract.users || []).find(user => user.owner) || null;
      },
      // The accountable owner first, then the other owners
      people() {
        let users = this.contract.users || [];
        return this.owner ? [this.owner].concat(users.filter(user => user.id !== this.owner.id)) : users;
      },
      checks() {
        let checks = [
          { key: 'third_party', ok: this.vendors.length > 0 },
          { key: 'owner', ok: !!this.owner },
          { key: 'description', ok: !!this.contract.description }
        ];
        // The privacy facts only count where the GDPR module and the contract's data privacy part are on
        if (this.getClient.gdprModule && this.contract.data_privacy_feature) {
          checks.push({ key: 'dpa', ok: !!this.contract.data_processor_agreement });
          checks.push({ key: 'locations', ok: !!(this.contract.processing_locations && this.contract.processing_locations.length) });
          checks.push({ key: 'transfers', ok: !!this.contract.transfers_disclosures_third_countries });
        }
        return checks;
      },
      done() {
        return this.checks.filter(check => check.ok).length;
      },
      ringDash() {
        let length = 2 * Math.PI * 32;
        return (length * this.done / this.checks.length) + ' ' + length;
      },
      missingText() {
        let missing = this.checks.filter(check => !check.ok);
        if (!missing.length) {
          return this.$t('system_hero.all_filled');
        }
        let names = missing.map(check => this.$t('contract_hero.missing.' + check.key).toLowerCase());
        return this.$t('system_hero.missing', { list: names.join(', ') });
      }
    },
    methods: {
      endedOn: endedOn,
      vendorRoute(vendor) {
        return { name: 'contracts.third_parties.view', params: { slug: vendor.slug } };
      },
      print() {
        window.print();
      }
    }
  }
</script>
